import React, { useCallback, useMemo, useRef } from 'react';
import {
  ClientModel,
  ClientQueryResponse,
  SubEntityModel,
  ClientContextValue,
  ClientContext,
} from '@cko/dashboard-shared';
import settings from 'services/app-settings';
import { useClientProviderFetcher } from '../../hooks/useClientProviderFetcher';

type ClientsCache = Record<string, ClientModel>;

export const ClientProvider = ({ children }: { children: React.ReactNode }) => {
  const usersApiBasePath = settings.api.userManagementApiBasePath;
  const clientsCache = useRef<ClientsCache>({});
  const { fetchGetClient, fetchGetInitialClient, fetchQueryClients } =
    useClientProviderFetcher(usersApiBasePath);

  const getClient = useCallback(
    async (clientId: string): Promise<ClientModel | undefined> => {
      let client: ClientModel | undefined = clientsCache.current[clientId];
      if (!client) {
        try {
          client = await fetchGetClient(clientId);
          if (!client) {
            throw new Error('no client returned');
          }
          clientsCache.current[clientId] = client;
        } catch {
          // eslint-disable-next-line no-console
          console.warn('Cannot fetch client');
        }
      }
      return client;
    },
    [fetchGetClient],
  );

  const queryClients = useCallback(
    async (query: string, limit?: number, skip?: number) => {
      try {
        const result: ClientQueryResponse | undefined = await fetchQueryClients(query, limit, skip);
        if (result) {
          const clients: ClientModel[] = result.data;
          clients.forEach((client: ClientModel) => {
            clientsCache.current[client.id] = client;
          });
          return result;
        }

        throw new Error('no response returned');
      } catch {
        // eslint-disable-next-line no-console
        console.warn('cannot fetch clients');
        return undefined;
      }
    },
    [fetchQueryClients],
  );

  const getInitialClient = useCallback(async () => {
    try {
      const result: ClientModel | SubEntityModel | undefined = await fetchGetInitialClient();
      if (result instanceof ClientModel) {
        clientsCache.current[result.id] = result;
      }
      return result;
    } catch {
      // eslint-disable-next-line no-console
      console.warn('cannot fetch clients');
      return undefined;
    }
  }, [fetchGetInitialClient]);

  Promise.resolve(undefined);

  const value = useMemo<ClientContextValue>(
    () => ({
      getClient,
      queryClients,
      getInitialClient,
    }),
    [getClient, queryClients, getInitialClient],
  );

  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
};
